import { useState } from 'react'
import Icon from './icons.jsx'
import { useApp } from '../context/AppContext.jsx'
import { setContactCustomFields } from '../db/api.js'
import { changedValues, formatFieldValue, inputValue } from '../lib/leadCustomFields.js'
import './leadCustomFields.css'

function FieldInput({ definition, value, onChange, disabled }) {
  const common = { className: 'lcf-input', value, disabled, 'aria-label': definition.label }
  if (definition.field_type === 'select') {
    return (
      <select {...common} onChange={(event) => onChange(event.target.value)}>
        <option value="">— vazio —</option>
        {(definition.options || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
      </select>
    )
  }
  if (definition.field_type === 'date') {
    return <input {...common} type="date" onChange={(event) => onChange(event.target.value)} />
  }
  return (
    <input
      {...common}
      type="text"
      inputMode={definition.field_type === 'number' ? 'decimal' : 'text'}
      maxLength={500}
      onChange={(event) => onChange(event.target.value)}
    />
  )
}

/**
 * Campos personalizados do lead (#306). `definitions` vem de GET /api/crm/lead-custom-fields e
 * `values` de `custom_fields` em GET /api/crm/contacts/{id}. Sem campos criados, não aparece.
 */
export default function LeadCustomFields({ contactId, definitions, values, onSaved, onManage }) {
  const { toast } = useApp()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState({})
  const [saving, setSaving] = useState(false)
  if (!definitions?.length) return null

  function startEdit() {
    setDraft(Object.fromEntries(definitions.map((def) => [def.key, inputValue(values?.[def.key])])))
    setEditing(true)
  }

  async function save() {
    const changes = changedValues(definitions, values, draft)
    if (!Object.keys(changes).length) { setEditing(false); return }
    setSaving(true)
    try {
      const res = await setContactCustomFields(contactId, changes)
      onSaved?.(res.custom_fields || {})
      setEditing(false)
      toast('Campos salvos.', 'success')
    } catch (err) {
      toast(err.message || 'Não foi possível salvar os campos.', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="lcf">
      <div className="cv-label">Campos do lead</div>
      {definitions.map((def) => (
        <div className="cv-row" key={def.key}>
          <div className="lcf-body">
            <div className="cv-row-label">{def.label}</div>
            {editing ? (
              <FieldInput
                definition={def}
                value={draft[def.key] ?? ''}
                disabled={saving}
                onChange={(value) => setDraft((prev) => ({ ...prev, [def.key]: value }))}
              />
            ) : (
              <div className="cv-row-value">{formatFieldValue(def, values?.[def.key]) || '—'}</div>
            )}
          </div>
        </div>
      ))}
      {editing ? (
        <div className="lcf-actions">
          <button type="button" className="cv-editfields" onClick={() => setEditing(false)} disabled={saving}>Cancelar</button>
          <button type="button" className="cv-editfields" onClick={save} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</button>
        </div>
      ) : (
        <div className="lcf-actions">
          <button type="button" className="cv-editfields" onClick={startEdit}>
            <Icon n="edit" s={13} /> Editar campos do lead
          </button>
          {onManage && (
            <button type="button" className="cv-editfields" onClick={onManage}>
              <Icon n="settings" s={13} /> Gerenciar campos
            </button>
          )}
        </div>
      )}
    </div>
  )
}
